import { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Trash2,
  RefreshCw,
  Package,
  ShoppingCart,
  X,
  Plus,
  Minus,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  addDailyRecord,
  getUserDailyRecords,
  getAllUsers,
  getAssociateUsers,
  getUsersByReferrer,
  getUserById,
} from "../../services/adminRechargeService";
import { addOrder, getUserOrders, getNextBillNumber, getNextDailySerialNumber } from "../../services/orderService";
import { updateUserDetails } from "../../services/userService";
import { fetchProducts } from "../../redux/slices/productSlice";
import { fetchCategories } from "../../redux/slices/categorySlice";
import {
  logOutwardTransaction,
  getOutwardTransactions,
  getTransactionSummary,
} from "../../services/transactionService";
import { CustomDropdown } from "../../components/common/CustomDropdown";
import AdminCard from "../../components/common/AdminCard";
import AdminInput from "../../components/common/AdminInput";
import InvoiceReceipt from "../../components/invoice/InvoiceReceipt";
import { getWeeklyShakeMenu } from "../../services/weeklyShakeMenuService";
import { addInventoryLog } from "../../services/inventoryLogService";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../database/firebase";

// Bundle pricing for daily shake combinations
const BUNDLE_PRICING = {
  1: 240,
  5: 1150,
  15: 3300,
  25: 5250,
};

// Named shake packages  (days → package info)
const SHAKE_PACKAGES = {
  1: { name: 'Starter', days: 1, totalPrice: 240, pricePerDay: 240 },
  5: { name: 'Growth', days: 5, totalPrice: 1150, pricePerDay: 230 },
  15: { name: 'Advanced', days: 15, totalPrice: 3300, pricePerDay: 220 },
  25: { name: 'Ultimate', days: 25, totalPrice: 5250, pricePerDay: 210 },
};

// Reverse lookup: package name → days
const PACKAGE_NAME_TO_DAYS = {
  Starter: 1,
  Growth: 5,
  Advanced: 15,
  Ultimate: 25,
};

/**
 * Returns the user's shake package info based on (priority order):
 *  1. inventory.shakePackage  (stored package name, e.g. 'Ultimate')
 *  2. inventory.topUpPlan     (stored days, legacy)
 *  3. Inferred from remainingShakes  (for users before this was saved)
 */
const getEffectivePlan = (user) => {
  if (!user) return null;

  // Priority 1: stored package name
  const pkgName = user.inventory?.shakePackage;
  if (pkgName && PACKAGE_NAME_TO_DAYS[pkgName]) {
    return PACKAGE_NAME_TO_DAYS[pkgName];
  }

  // Priority 2: stored days plan (legacy field)
  const storedDays = user.inventory?.topUpPlan;
  if (storedDays && BUNDLE_PRICING[storedDays]) {
    return storedDays;
  }

  // Priority 3: infer from remaining shakes
  const shakes = user.inventory?.remainingShakes || 0;
  if (shakes > 15) return 25;
  if (shakes > 5) return 15;
  if (shakes > 1) return 5;
  if (shakes >= 1) return 1;
  return null;
};

/** Helper: get the package name for a given plan (days) */
const getPackageName = (days) => SHAKE_PACKAGES[days]?.name || `${days} Day`;

const AdminRecharge = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user: currentUser } = useSelector((state) => state.auth);
  const { products } = useSelector((state) => state.product);
  const { categories } = useSelector((state) => state.category);

  // Transaction Type State
  const [transactionType, setTransactionType] = useState("Guest Shake");
  const [referredByUser, setReferredByUser] = useState(null);
  const [showReferredByDropdown, setShowReferredByDropdown] = useState(false);
  const [referredBySearchTerm, setReferredBySearchTerm] = useState("");

  // User Search State
  const [searchTerm, setSearchTerm] = useState("");
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // Category & Product Selection State
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCategoryType, setSelectedCategoryType] = useState('day');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [weeklyMenu, setWeeklyMenu] = useState({});

  // Success Modal State
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [orderSummary, setOrderSummary] = useState(null);

  // Duration Modal State
  const [showDurationModal, setShowDurationModal] = useState(false);
  const [selectedProductForDuration, setSelectedProductForDuration] = useState(null);
  const [selectedDiscount, setSelectedDiscount] = useState(0);
  const [modalStep, setModalStep] = useState('discount'); // 'discount' or 'duration'
  const [isBundleSelection, setIsBundleSelection] = useState(false);

  // Daily Record State
  const [dailyRecord, setDailyRecord] = useState({
    date: new Date().toISOString().split("T")[0],
    weight: "",
    fat: "",
    visceralFat: "",
    muscles: "",
    bmr: "",
    bmi: "",
    bodyAge: "",
    chest: "",
    waistLine: "",
    hip: "",
    height: "",
  });

  // User Records State
  const [userRecords, setUserRecords] = useState({ average: 0, records: [] });
  const [lastOrderWeight, setLastOrderWeight] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showMobileCart, setShowMobileCart] = useState(false);

  // Payment Method State
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [isSplitPayment, setIsSplitPayment] = useState(false);
  const [splitAmounts, setSplitAmounts] = useState({ cash: "", online: "", gpay: "" });

  // Complementary shake usage tracking (max 3 per user)
  const [complementaryShakesUsed, setComplementaryShakesUsed] = useState(0);

  // State for editing special type in cart
  const [editingSpecialTypeId, setEditingSpecialTypeId] = useState(null);

  // Force payment method to match transaction type if it is Complementary or Donation
  useEffect(() => {
    if (transactionType === "Trial") {
      setPaymentMethod("Trial");
    } else if (transactionType === "Donation") {
      setPaymentMethod("donation");
    } else if (paymentMethod === "Trial" || paymentMethod === "donation") {
      setPaymentMethod("cash"); // Revert to default if switching away from special types
    }
  }, [transactionType]);

  // Dashboard Statistics State
  const [dashboardStats, setDashboardStats] = useState({
    totalRecharges: 0,
    totalAmount: 0,
    totalValuePoints: 0,
    recentTransactions: [],
  });

  // Current Day State
  const [currentDay] = useState(() => new Date().getDay());
  const [currentDayName] = useState(() => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[new Date().getDay()];
  });

  // Current day as a category object
  const [currentDayCategory] = useState(() => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = days[new Date().getDay()];
    const dayLabels = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return {
      key: dayName,
      label: dayLabels[new Date().getDay()],
      type: 'day'
    };
  });

  // Load products, categories, weekly menu, dashboard stats, and users on mount
  useEffect(() => {
    dispatch(fetchProducts());
    dispatch(fetchCategories());
    loadDashboardStats();
    loadUsers();
    loadWeeklyMenu();
  }, [dispatch]);

  // Load weekly shake menu from Firestore
  const loadWeeklyMenu = async () => {
    try {
      const menu = await getWeeklyShakeMenu();
      setWeeklyMenu(menu);
      // Auto-select current day
      setSelectedCategory(currentDayCategory);
      setSelectedCategoryType('day');
    } catch (error) {
      console.error('Error loading weekly menu:', error);
    }
  };

  // Load users based on transaction type
  const loadUsers = async () => {
    setIsLoadingUsers(true);
    try {
      let users = [];

      // Determine which users to load based on transaction type
      if (
        transactionType === "TopUp Associate Shake" ||
        transactionType === "Associate Shake"
      ) {
        users = await getAssociateUsers();
      } else {
        users = await getAllUsers();
      }

      setAllUsers(users);
      setFilteredUsers(users);
    } catch (error) {
      console.error("Error loading users:", error);
      toast.error("Error loading users");
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Reload users when transaction type changes
  useEffect(() => {
    loadUsers();
  }, [transactionType]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".dropdown-container")) {
        setShowDropdown(false);
        setShowReferredByDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Load Dashboard Statistics
  const loadDashboardStats = async () => {
    try {
      const outwardTransactions = await getOutwardTransactions();
      const summary = getTransactionSummary(outwardTransactions);

      setDashboardStats({
        totalRecharges: summary.outwardCount,
        totalAmount: summary.totalOutward,
        totalValuePoints: summary.totalOutwardPoints,
        recentTransactions: outwardTransactions.slice(0, 5), // Last 5 transactions
      });
    } catch (error) {
      console.error("Error loading dashboard stats:", error);
    }
  };

  // Count how many complementary shakes this user has already used
  useEffect(() => {
    if (!selectedUser) {
      setComplementaryShakesUsed(0);
      return;
    }
    // Read directly from user inventory instead of fetching orders
    const consumed = Number(selectedUser.inventory?.complementaryShakeQuantity || 0);
    setComplementaryShakesUsed(consumed);
  }, [selectedUser]);

  // Auto-select current day when component mounts
  useEffect(() => {
    if (!selectedCategory && currentDayCategory) {
      setSelectedCategory(currentDayCategory);
      setSelectedCategoryType('day');
    }
  }, [currentDayCategory, selectedCategory]);

  // Filter products based on selected category type
  const filteredProducts = useMemo(() => {
    if (!selectedCategory) return [];

    if (selectedCategoryType === 'day') {
      // Day category - show all shakes for that day
      const dayMenu = weeklyMenu[selectedCategory.key];
      if (!dayMenu) return [];

      const shakeIds = [
        dayMenu.shake1?.id,
        dayMenu.shake2?.id,
        dayMenu.shake3?.id,
        dayMenu.shake4?.id,
      ].filter(Boolean);

      return products.filter(product => shakeIds.includes(product.id));
    } else {
      // Product category - show all products in that category
      return products.filter(product =>
        product.categoryId === selectedCategory.id
      );
    }
  }, [selectedCategory, selectedCategoryType, weeklyMenu, products]);

  // Special products — always shown regardless of selected day/category
  const specialProducts = useMemo(() => {
    return products.filter(p => p.isSpecial === true);
  }, [products]);

  console.log(filteredProducts, "selectedCategory");
  // Filter users based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredUsers(allUsers);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = allUsers.filter((user) => {
      const name = (user.personalInformation?.fullName || user.personalDetails?.name || "").toLowerCase();
      const mobile = user.personalInformation?.mobileNumber || user.personalDetails?.mobile || "";
      const email = (user.personalInformation?.email || user.personalDetails?.email || "").toLowerCase();

      return (
        name.includes(term) || mobile.includes(term) || email.includes(term)
      );
    });

    setFilteredUsers(filtered);
  }, [searchTerm, allUsers]);

  // Recalculate cart prices dynamically if user or transaction type changes
  useEffect(() => {
    if (selectedProducts.length === 0) return;

    const isConsumptionType = transactionType === "Guest Shake" || transactionType === "Associate Shake";

    const userTopUpPlan = isConsumptionType ? getEffectivePlan(selectedUser) : null;
    const usesPlanRate = isConsumptionType && userTopUpPlan && BUNDLE_PRICING[userTopUpPlan];
    const planPerDayRate = usesPlanRate ? BUNDLE_PRICING[userTopUpPlan] / userTopUpPlan : null;

    setSelectedProducts(prev => {
      let changed = false;
      const updated = prev.map(product => {
        if (!product.isDayBased && !product.isBundle) return product;

        const days = product.days || product.quantity || 1;
        const newBundlePrice = usesPlanRate
          ? Math.round(planPerDayRate * days)
          : BUNDLE_PRICING[days] || 0;

        if (product.totalPrice !== newBundlePrice) {
          changed = true;
          return {
            ...product,
            productPrice: newBundlePrice / days,
            originalPrice: newBundlePrice / days,
            totalPrice: newBundlePrice,
            ...(product.isBundle && {
              bundleProducts: product.bundleProducts?.map(bp => ({
                ...bp,
                price: (newBundlePrice / days) / (product.bundleProducts.length || 2)
              }))
            })
          };
        }
        return product;
      });
      return changed ? updated : prev;
    });
  }, [selectedUser, transactionType]);

  // Handle user selection
  const handleSelectUser = async (user) => {
    setSelectedUser(user);
    setSearchTerm(user.personalInformation?.fullName || user.personalDetails?.name || "");
    setShowDropdown(false);

    // Auto-select referredBy from user data
    const referrerName = user.accountSecurity?.referredBy || user.referredBy || user.sponsoredBy || "";
    if (referrerName) {
      const matchingReferrer = allUsers.find(u => {
        const uName = (u.personalInformation?.fullName || u.personalDetails?.name || u.name || "").toLowerCase();
        return uName === referrerName.toLowerCase() || u.id === referrerName;
      });

      if (matchingReferrer) {
        const matchedName = matchingReferrer.personalInformation?.fullName || matchingReferrer.personalDetails?.name || matchingReferrer.name || referrerName;
        setReferredBySearchTerm(matchedName);
        setReferredByUser({ name: matchedName, id: matchingReferrer.id });
      } else {
        setReferredBySearchTerm(referrerName);
        setReferredByUser({ name: referrerName, id: null });
      }
    } else if (!referredByUser) {
      setReferredBySearchTerm("");
      setReferredByUser(null);
    }

    // Load user's daily records
    try {
      const records = await getUserDailyRecords(user.id);
      setUserRecords(records);

      // Auto-populate height from user data or latest record
      const userHeight = user.wellnessProfile?.bodyMetrics?.current?.height || user.height || (records.records && records.records.length > 0 ? records.records[0].height : "");
      if (userHeight) {
        setDailyRecord(prev => ({
          ...prev,
          height: userHeight.toString()
        }));
      }
    } catch (error) {
      console.error("Error loading user records:", error);
    }

    // Load user's last order to get latest weight
    try {
      const orders = await getUserOrders(user.id);
      if (orders && orders.length > 0) {
        const lastOrder = orders[0]; // Already sorted by newest first
        if (lastOrder.dailyHealthRecord?.weightKg) {
          setLastOrderWeight(lastOrder.dailyHealthRecord.weightKg);
        } else {
          setLastOrderWeight(null);
        }
      } else {
        setLastOrderWeight(null);
      }
    } catch (error) {
      console.error("Error loading user orders:", error);
      setLastOrderWeight(null);
    }
  };

  // Handle referred by user selection
  const handleReferredBySelect = async (user) => {
    setReferredByUser(user);
    setShowReferredByDropdown(false);

    // Load users referred by this user
    try {
      const referredUsers = await getUsersByReferrer(user.id);
      setAllUsers(referredUsers);
      setFilteredUsers(referredUsers);
      setSelectedUser(null);
      setSearchTerm("");
    } catch (error) {
      console.error("Error loading referred users:", error);
      toast.error("Error loading referred users");
    }
  };

  // Clear referred by filter
  const clearReferredByFilter = () => {
    setReferredByUser(null);
    setReferredBySearchTerm("");
    setFilteredUsers(allUsers);
    loadUsers();
  };

  // Get filtered referrers for the Referred By autocomplete
  const getFilteredReferrers = (searchTerm) => {
    if (!searchTerm) return allUsers.slice(0, 50);
    const term = searchTerm.toLowerCase();
    return allUsers.filter(u => {
      const name = (u.personalInformation?.fullName || u.personalDetails?.name || u.name || "").toLowerCase();
      const mobile = u.personalInformation?.mobileNumber || u.personalDetails?.mobile || u.mobile || "";
      const customerNumber = (u.customerNumber || u.accountSecurity?.herbalifeId || u.herbalifeId || "").toLowerCase();

      return name.includes(term) || mobile.includes(term) || customerNumber.includes(term);
    }).slice(0, 50);
  };

  // Handle Daily Record Change
  const handleDailyRecordChange = (field, value) => {
    setDailyRecord((prev) => ({
      ...prev,
      [field]: value,
    }));
  };


  // Handle Category Selection
  const handleCategorySelect = (category, type = 'product') => {
    setSelectedCategory(category);
    setSelectedCategoryType(type);
  };

  // Handle Product Selection - Open Duration Modal
  const handleProductSelect = (product, isDayBased = false) => {
    if (product.isSpecial) {
      const existingProduct = selectedProducts.find(p => p.productId === product.id);
      const originalPrice = parseFloat(product.mrp || product.price || 0);
      const discountedPrice = 0; // 100% discount
      const volPoint = parseFloat(product.volPoint || 0);
      const days = 1;
      const discount = 100;

      if (existingProduct) {
        setSelectedProducts(
          selectedProducts.map((p) =>
            p.productId === product.id
              ? {
                ...p,
                quantity: existingProduct.quantity + 1,
                totalPrice: (existingProduct.quantity + 1) * discountedPrice,
                productPrice: discountedPrice,
                originalPrice: originalPrice,
                discount: discount,
                volPoint: volPoint,
              }
              : p
          )
        );
      } else {
        const newProduct = {
          id: Date.now(),
          date: new Date().toISOString().split("T")[0],
          productId: product.id,
          productName: product.name || product.productName || "",
          quantity: days,
          days: days,
          productPrice: discountedPrice,
          originalPrice: originalPrice,
          discount: discount,
          totalPrice: days * discountedPrice,
          volPoint: volPoint,
          isDayBased: isDayBased,
          specialType: "Trial",
        };
        setSelectedProducts([...selectedProducts, newProduct]);
      }
      toast.success(`${product.name || product.productName} added as TRIAL!`);
      return;
    }

    setSelectedProductForDuration(product);
    setSelectedDiscount(0);

    // Guest Shake, Associate Shake, and their TopUp variants always use bundle pricing (₹210/day for 25d)
    const isShakeTransaction =
      transactionType === 'Guest Shake' ||
      transactionType === 'Associate Shake' ||
      transactionType === 'TopUp Guest Shake' ||
      transactionType === 'TopUp Associate Shake';
    setIsBundleSelection(isDayBased || isShakeTransaction);

    // Only show discount step for RetailShake
    if (transactionType === 'RetailShake') {
      setModalStep('discount');
    } else {
      setModalStep('duration');
    }

    setShowDurationModal(true);
  };

  // Handle Bundle Selection - Skip discount, go straight to duration
  const handleBundleSelect = (products) => {
    setSelectedProductForDuration(products); // Array of 2 products
    setSelectedDiscount(0);
    setIsBundleSelection(true);
    setModalStep('duration');
    setShowDurationModal(true);
  };

  // Handle Discount Selection (Step 1)
  const handleDiscountSelect = (discountPercent) => {
    setSelectedDiscount(discountPercent);

    // If RetailShake, skip duration selection and assume 1 day
    if (transactionType === 'RetailShake') {
      handleDurationSelect(1);
    } else {
      setModalStep('duration');
    }
  };

  // Handle Duration Selection (Step 2)
  const handleDurationSelect = (days) => {
    if (!selectedProductForDuration) return;

    // Handle bundle selection (both actual bundles and day-based individual shakes)
    if (isBundleSelection) {
      // Check if it's an array (actual bundle) or single product (day-based shake)
      const isActualBundle = Array.isArray(selectedProductForDuration);

      // For consumption (Guest Shake / Associate Shake), bill at the user's top-up plan rate.
      // getEffectivePlan() uses stored topUpPlan OR infers from remainingShakes —
      // so existing users who topped up before topUpPlan was saved also get the right rate.
      const userTopUpPlan = isConsumptionType ? getEffectivePlan(selectedUser) : null;
      const usesPlanRate =
        isConsumptionType && userTopUpPlan && BUNDLE_PRICING[userTopUpPlan];
      const planPerDayRate = usesPlanRate
        ? BUNDLE_PRICING[userTopUpPlan] / userTopUpPlan
        : null;
      const bundlePrice = usesPlanRate
        ? Math.round(planPerDayRate * days)
        : BUNDLE_PRICING[days] || 0;

      if (isActualBundle) {
        // Original bundle logic for 2 products
        const combinedName = selectedProductForDuration
          .map(p => p.name || p.productName)
          .join(' + ');

        const totalVolPoints = selectedProductForDuration.reduce(
          (acc, p) => acc + parseFloat(p.volPoint || 0),
          0
        );

        const newBundleProduct = {
          id: Date.now(),
          date: new Date().toISOString().split("T")[0],
          productId: "bundle_" + selectedProductForDuration.map(p => p.id).join("_"),
          productName: combinedName,
          quantity: days,
          days: days,
          productPrice: bundlePrice / days,
          originalPrice: bundlePrice / days,
          discount: 0,
          totalPrice: bundlePrice,
          volPoint: totalVolPoints,
          isBundle: true,
          bundleProducts: selectedProductForDuration.map(p => ({
            productId: p.id,
            productName: p.name || p.productName,
            price: (bundlePrice / days) / 2,
            volPoint: parseFloat(p.volPoint || 0)
          }))
        };

        setSelectedProducts([...selectedProducts, newBundleProduct]);

        const bundleNames = selectedProductForDuration
          .map(p => p.name || p.productName)
          .join(' + ');

        toast.success(
          `${bundleNames} bundle added for ${days} day${days > 1 ? 's' : ''} - ₹${bundlePrice.toLocaleString()}!`
        );
      } else {
        // Day-based individual shake with bundle pricing
        const product = selectedProductForDuration;
        const volPoint = parseFloat(product.volPoint || 0);

        const newProduct = {
          id: Date.now(),
          date: new Date().toISOString().split("T")[0],
          productId: product.id,
          productName: product.name || product.productName || "",
          quantity: days,
          days: days,
          productPrice: bundlePrice / days,
          originalPrice: bundlePrice / days,
          discount: 0,
          totalPrice: bundlePrice,
          volPoint: volPoint,
          isDayBased: true
        };

        setSelectedProducts([...selectedProducts, newProduct]);

        toast.success(
          `${product.name || product.productName} added for ${days} day${days > 1 ? 's' : ''} - ₹${bundlePrice.toLocaleString()}!`
        );
      }

      setShowDurationModal(false);
      setSelectedProductForDuration(null);
      setSelectedDiscount(0);
      setIsBundleSelection(false);
      setModalStep('discount');
      return;
    }

    // Regular product selection (non-day-based)
    const product = selectedProductForDuration;
    const existingProduct = selectedProducts.find(
      (p) => p.productId === product.id
    );
    const originalPrice = parseFloat(product.mrp || product.price || 0);
    const discountedPrice = originalPrice * (1 - selectedDiscount / 100);
    const volPoint = parseFloat(product.volPoint || 0);

    if (existingProduct) {
      // Update quantity to match days
      setSelectedProducts(
        selectedProducts.map((p) =>
          p.productId === product.id
            ? {
              ...p,
              quantity: days,
              days: days,
              totalPrice: days * discountedPrice,
              productPrice: discountedPrice,
              originalPrice: originalPrice,
              discount: selectedDiscount,
              volPoint: volPoint,
            }
            : p
        )
      );
    } else {
      // Add new product with quantity = days
      const newProduct = {
        id: Date.now(),
        date: new Date().toISOString().split("T")[0],
        productId: product.id,
        productName: product.name || product.productName || "",
        quantity: days,
        days: days,
        productPrice: discountedPrice,
        originalPrice: originalPrice,
        discount: selectedDiscount,
        totalPrice: days * discountedPrice,
        volPoint: volPoint,
      };
      setSelectedProducts([...selectedProducts, newProduct]);
    }

    toast.success(
      `${product.name || product.productName
      } added for ${days} day${days > 1 ? 's' : ''} with ${selectedDiscount}% discount!`
    );
    setShowDurationModal(false);
    setSelectedProductForDuration(null);
    setSelectedDiscount(0);
    setModalStep('discount');
  };

  // Update Selected Product Quantity
  const handleUpdateQuantity = (id, change) => {
    setSelectedProducts(
      selectedProducts
        .map((product) => {
          if (product.id === id) {
            const newQuantity = Math.max(0, product.quantity + change);
            if (newQuantity === 0) return null;
            return {
              ...product,
              quantity: newQuantity,
              totalPrice: newQuantity * product.productPrice,
            };
          }
          return product;
        })
        .filter(Boolean)
    );
  };

  // Remove Selected Product
  const handleRemoveProduct = (id) => {
    setSelectedProducts(selectedProducts.filter((p) => p.id !== id));
  };

  // Edit Selected Product
  const handleEditProduct = (cartItem) => {
    const originalProduct = products.find(p => p.id === cartItem.productId);
    if (!originalProduct) {
      toast.error("Product details not found for editing");
      return;
    }

    setSelectedProductForDuration(originalProduct);
    setSelectedDiscount(cartItem.discount || 0);
    setIsBundleSelection(cartItem.isDayBased || false);

    setModalStep('discount'); // Allow changing pricing and duration
    setShowDurationModal(true);
  };

  // Calculate Total with Free Shake Logic
  const orderTotal = (() => {
    // Check if this is a consumption transaction (Guest Shake, Associate Shake, or Donation)
    const isConsumptionTransaction =
      transactionType === "Guest Shake" ||
      transactionType === "Associate Shake" ||
      transactionType === "Donation" ||
      transactionType === "Trial";

    // Get user's available shake quantity from new structure
    const availableShakeQuantity = selectedUser?.inventory?.remainingShakes || 0;

    // Calculate totals
    const totals = selectedProducts.reduce(
      (acc, product) => ({
        quantity: acc.quantity + product.quantity,
        billableQuantity: acc.billableQuantity + (product.discount === 100 ? 0 : product.quantity),
        amount: acc.amount + product.totalPrice,
        valuePoints:
          acc.valuePoints + (product.volPoint || 0) * product.quantity,
      }),
      { quantity: 0, billableQuantity: 0, amount: 0, valuePoints: 0 }
    );

    // If it's a Donation or Complementary transaction, always charge ₹0
    if (transactionType === "Donation" || transactionType === "Trial") {
      return {
        quantity: totals.quantity,
        amount: 0,
        originalAmount: 0,
        valuePoints: totals.valuePoints,
        freeShakesUsed: 0,
        chargedQuantity: 0,
      };
    }

    // If it's a consumption transaction and user has available shakes
    if (isConsumptionTransaction && availableShakeQuantity > 0) {
      const totalBillableQuantity = totals.billableQuantity;

      // If user has enough shakes to cover the billable order, charge ₹0
      if (availableShakeQuantity >= totalBillableQuantity) {
        return {
          quantity: totals.quantity,
          amount: 0,
          originalAmount: totals.amount,
          valuePoints: totals.valuePoints,
          freeShakesUsed: totalBillableQuantity,
          chargedQuantity: 0,
        };
      } else {
        // User has some shakes but not enough
        // Charge only for the excess quantity
        const chargedQuantity = totalBillableQuantity - availableShakeQuantity;
        const chargePercentage = totalBillableQuantity > 0 ? chargedQuantity / totalBillableQuantity : 0;
        const chargedAmount = totals.amount * chargePercentage;

        return {
          quantity: totals.quantity,
          amount: chargedAmount,
          originalAmount: totals.amount,
          valuePoints: totals.valuePoints,
          freeShakesUsed: availableShakeQuantity,
          chargedQuantity: chargedQuantity,
        };
      }
    }

    // For top-up transactions or when no free shakes available, charge full amount
    return {
      ...totals,
      originalAmount: totals.amount,
      freeShakesUsed: 0,
      chargedQuantity: totals.quantity,
    };
  })();

  // Derived: warn when order quantity exceeds remaining shakes
  const availableShakes = selectedUser?.inventory?.remainingShakes || 0;
  const orderedQuantity = selectedProducts.reduce((sum, p) => sum + p.quantity, 0);
  const isConsumptionType =
    transactionType === "Guest Shake" ||
    transactionType === "Associate Shake";
  const lowQuantityWarning =
    isConsumptionType &&
    selectedUser &&
    orderedQuantity > availableShakes &&
    orderedQuantity > 0;

  // Place Order
  const handlePlaceOrder = async () => {
    if (!selectedUser) {
      toast.error("Please select a user first");
      return;
    }

    if (selectedProducts.length === 0) {
      toast.error("Please select at least one product");
      return;
    }

    let finalPaymentMethodStr = paymentMethod;
    if (transactionType !== "Trial" && transactionType !== "Donation" && isSplitPayment) {
      const sum = Object.values(splitAmounts).reduce((acc, val) => acc + (parseFloat(val) || 0), 0);
      if (Math.abs(sum - orderTotal.amount) > 0.1) {
        toast.error("Split payment total must match the total amount.");
        return;
      }
      const usedMethods = Object.entries(splitAmounts).filter(([_, val]) => parseFloat(val) > 0);
      if (usedMethods.length === 0) {
        toast.error("Please enter payment amounts.");
        return;
      }
      finalPaymentMethodStr = usedMethods.map(([method, val]) => `${method}(₹${parseFloat(val).toFixed(0)})`).join(", ");
    }

    // Block Complementary orders if user has already used 3 shakes
    if (transactionType === "Trial" && complementaryShakesUsed >= 3) {
      toast.error("This user has already used all 3 TRIAL shakes");
      return;
    }

    // Also warn if adding more would exceed the limit
    if (transactionType === "Trial") {
      const newTotal = selectedProducts.reduce((sum, p) => sum + p.quantity, 0);
      if (complementaryShakesUsed + newTotal > 3) {
        toast.error(`Only ${3 - complementaryShakesUsed} TRIAL shake(s) remaining for this user`);
        return;
      }
    }

    setIsLoading(true);
    try {
      // ── Pre-compute everything synchronously ──────────────────────────────
      const hasDailyRecordData =
        dailyRecord.weight ||
        dailyRecord.fat ||
        dailyRecord.visceralFat ||
        dailyRecord.muscles ||
        dailyRecord.bmr ||
        dailyRecord.bmi ||
        dailyRecord.bodyAge ||
        dailyRecord.chest ||
        dailyRecord.waistLine ||
        dailyRecord.hip;

      const totalShakeQuantity = selectedProducts.filter(p => p.discount !== 100).reduce((sum, p) => sum + p.quantity, 0);
      const currentShakeBalance = selectedUser.inventory?.remainingShakes || 0;

      const isTopUpTransaction =
        transactionType === "TopUp Guest Shake" ||
        transactionType === "TopUp Associate Shake";

      const isConsumptionTransactionForBalance =
        transactionType === "Guest Shake" ||
        transactionType === "Associate Shake" ||
        transactionType === "Donation";

      const isConsumptionTransaction =
        transactionType === "Guest Shake" ||
        transactionType === "Associate Shake" ||
        transactionType === "Trial" ||
        transactionType === "RetailShake" ||
        transactionType === "Donation";

      let shakeBalanceChange = 0;
      let newShakeBalance = currentShakeBalance;
      let quantityChange = "0";
      let consumedOnTopUp = 0; // how many shakes consumed on the day of top-up

      if (isTopUpTransaction) {
        // TopUp: consume 1 today + add the purchased quantity
        consumedOnTopUp = Math.min(1, currentShakeBalance + totalShakeQuantity); // consume 1 (the today's shake)
        shakeBalanceChange = totalShakeQuantity - consumedOnTopUp;
        newShakeBalance = Math.max(0, currentShakeBalance + shakeBalanceChange);
        quantityChange = shakeBalanceChange > 0 ? `+${shakeBalanceChange}` : `${shakeBalanceChange}`;
      } else if (isConsumptionTransactionForBalance) {
        shakeBalanceChange = -totalShakeQuantity;
        newShakeBalance = Math.max(0, currentShakeBalance + shakeBalanceChange);
        quantityChange = shakeBalanceChange > 0 ? `+${shakeBalanceChange}` : `${shakeBalanceChange}`;
      }

      const dailyHealthRecord = hasDailyRecordData ? {
        weightKg: parseFloat(dailyRecord.weight) || 0,
        heightCm: parseFloat(dailyRecord.height) || 0,
        bodyFatPct: parseFloat(dailyRecord.fat) || 0,
        visceralFat: parseFloat(dailyRecord.visceralFat) || 0,
        muscleMassPct: parseFloat(dailyRecord.muscles) || 0,
        bmrKcal: parseFloat(dailyRecord.bmr) || 0,
        bodyAge: parseInt(dailyRecord.bodyAge) || 0,
        chestIn: parseFloat(dailyRecord.chest) || 0,
        waistLineIn: parseFloat(dailyRecord.waistLine) || 0,
        hipIn: parseFloat(dailyRecord.hip) || 0,
        bmi: parseFloat(dailyRecord.bmi) || 0,
      } : null;

      const items = selectedProducts.map((p) => ({
        productId: p.productId,
        productName: p.productName,
        quantity: p.quantity,
        pricePerUnit: p.productPrice,
        originalPrice: p.originalPrice || p.productPrice,
        totalPrice: p.totalPrice,
        valuePointPerUnit: p.volPoint || 0,
        totalValuePoints: (p.volPoint || 0) * p.quantity,
        ...(p.isBundle && { isBundle: true }),
        ...(p.bundleProducts && { bundleProducts: p.bundleProducts }),
        ...(p.days && { days: p.days }),
        ...(p.discount && { discount: p.discount }),
        ...(p.specialType && { specialType: p.specialType }),
      }));

      // ── CRITICAL PATH (blocks modal) ─────────────────────────────────────
      // Run shake-balance update + bill-number fetch in parallel
      // ── CRITICAL PATH (blocks modal) ─────────────────────────────────────
      // Run shake-balance update + bill-number fetch + daily serial fetch in parallel
      const [billNum, dailySerialNum] = await Promise.all([
        getNextBillNumber(),
        getNextDailySerialNumber(),
        (isTopUpTransaction || isConsumptionTransactionForBalance || transactionType === "Trial")
          ? updateUserDetails(selectedUser.id, {
            ...((isTopUpTransaction || isConsumptionTransactionForBalance) && {
              'inventory.remainingShakes': newShakeBalance
            }),
            ...(isTopUpTransaction && {
              'inventory.lastTopUpDate': new Date().toISOString(),
              'inventory.topUpPlan': totalShakeQuantity,
              'inventory.shakePackage': getPackageName(totalShakeQuantity),
              'inventory.hasPurchasedPackage': true,
            }),
            ...(transactionType === "Trial" && {
              'inventory.complementaryShakeQuantity': complementaryShakesUsed + totalShakeQuantity
            }),
          })
          : Promise.resolve(),
        hasDailyRecordData
          ? addDailyRecord(selectedUser.id, {
            date: dailyRecord.date || new Date().toISOString().split("T")[0],
            weight: parseFloat(dailyRecord.weight) || 0,
            height: parseFloat(dailyRecord.height) || 0,
            fat: parseFloat(dailyRecord.fat) || 0,
            visceralFat: parseFloat(dailyRecord.visceralFat) || 0,
            muscles: parseFloat(dailyRecord.muscles) || 0,
            bmr: parseFloat(dailyRecord.bmr) || 0,
            bmi: parseFloat(dailyRecord.bmi) || 0,
            bodyAge: parseInt(dailyRecord.bodyAge) || 0,
            chest: parseFloat(dailyRecord.chest) || 0,
            waistLine: parseFloat(dailyRecord.waistLine) || 0,
            hip: parseFloat(dailyRecord.hip) || 0,
          })
          : Promise.resolve(),
      ]);

      const orderId = String(billNum);
      const now = new Date();

      if (quantityChange !== "0") {
        if (isTopUpTransaction && consumedOnTopUp > 0) {
          toast.success(
            `TopUp: −${consumedOnTopUp} consumed today, +${totalShakeQuantity} added → Balance: ${newShakeBalance} shakes`
          );
        } else {
          toast.success(
            `Shake quantity updated: ${quantityChange} (${currentShakeBalance} → ${newShakeBalance})`
          );
        }
      }

      // ── SHOW INVOICE IMMEDIATELY ──────────────────────────────────────────
      // For TopUp: item slip shows only today's 1 consumed shake (qty=1 per product)
      const itemSlipProducts = isTopUpTransaction && consumedOnTopUp > 0
        ? selectedProducts.map(p => ({ ...p, quantity: 1 }))
        : selectedProducts;

      const summary = {
        billNo: orderId,
        user: {
          name: selectedUser.personalInformation?.fullName || selectedUser.personalDetails?.name || "N/A",
          mobile: selectedUser.personalInformation?.mobileNumber || selectedUser.personalDetails?.mobile || "N/A",
          email: selectedUser.personalInformation?.email || selectedUser.personalDetails?.email || "N/A",
        },
        transactionType,
        dailyRecord: hasDailyRecordData ? { ...dailyRecord } : null,
        products: selectedProducts,       // 1st bill: full TopUp quantity (e.g. 5)
        itemSlipProducts,                 // 2nd bill: 1 shake (today's consumption)
        total: orderTotal,
        paymentMethod: finalPaymentMethodStr,
        shakeQuantityChange: quantityChange !== "0" ? quantityChange : undefined,
        previousShakeQuantity: (isTopUpTransaction || isConsumptionTransactionForBalance) ? currentShakeBalance : undefined,
        newShakeQuantity: (isTopUpTransaction || isConsumptionTransactionForBalance) ? newShakeBalance : undefined,
        dailySerial: dailySerialNum,
        // TopUp-specific breakdown for the bill
        ...(isTopUpTransaction && consumedOnTopUp > 0 && {
          topUpAdded: totalShakeQuantity,
          consumedOnTopUp,
          netShakeChange: shakeBalanceChange,
        }),
        date: now.toLocaleDateString("en-IN", {
          day: "2-digit", month: "short", year: "numeric",
          hour: "2-digit", minute: "2-digit",
        }),
      };
      setOrderSummary(summary);
      setShowSuccessModal(true);
      setIsLoading(false);

      // Clear products and payment info, but KEEP user selected
      setTimeout(() => {
        setSelectedProducts([]);
        setPaymentMethod("cash");
        setIsSplitPayment(false);
        setSplitAmounts({ cash: "", online: "", gpay: "" });
        setDailyRecord({
          date: new Date().toISOString().split("T")[0],
          weight: "",
          fat: "",
          visceralFat: "",
          muscles: "",
          bmr: "",
          bmi: "",
          bodyAge: "",
          chest: "",
          waistLine: "",
          hip: "",
          height: "",
        });
      }, 500);

      // ── BACKGROUND TASKS (non-blocking) ───────────────────────────────────
      const shakeBalanceChangeNum = quantityChange !== "0"
        ? parseInt(quantityChange.replace('+', ''))
        : 0;

      const bgTasks = [
        // Save order document (the TopUp / main transaction)
        addOrder({
          orderId,
          billNo: orderId,
          userId: selectedUser.id,
          timestamp: now.toISOString(),
          transaction: { type: transactionType, paymentMethod: finalPaymentMethodStr, status: "completed" },
          items,
          dailySerial: dailySerialNum,
          ...(dailyHealthRecord && { dailyHealthRecord }),
          summary: {
            grandTotalAmount: orderTotal.amount,
            totalOrderVP: orderTotal.valuePoints,
            // For TopUp: record the full purchased qty (e.g. 25), not the net after consumption (24)
            shakeBalanceChange: isTopUpTransaction ? totalShakeQuantity : shakeBalanceChangeNum,
            remainingShakesAfter: newShakeBalance,
          },
        }).catch((e) => {
          console.error("Failed to save order:", e);
          toast.error("Order placed but failed to save to Orders collection");
        }),
      ];

      // ── Additional consumption record for the shake used on TopUp day ──────
      // When a TopUp is placed, 1 shake is consumed immediately.
      // We save a SECOND order entry so it appears in the Recharge history.
      if (isTopUpTransaction && consumedOnTopUp > 0) {
        const consumptionType =
          transactionType === "TopUp Associate Shake"
            ? "Associate Shake"
            : "Guest Shake";

        // Item list with quantity capped to consumedOnTopUp (= 1)
        const consumptionItems = items.map((item) => ({
          ...item,
          quantity: consumedOnTopUp,
          totalPrice: 0, // no extra charge — already paid in the TopUp
          pricePerUnit: 0,
        }));

        bgTasks.push(
          getNextBillNumber()
            .then((consumptionBillNum) =>
              addOrder({
                orderId: String(consumptionBillNum),
                billNo: String(consumptionBillNum),
                userId: selectedUser.id,
                dailySerial: dailySerialNum,
                // 1 second after the TopUp so it sorts after it
                timestamp: new Date(now.getTime() + 1000).toISOString(),
                transaction: {
                  type: consumptionType,
                  paymentMethod: finalPaymentMethodStr,
                  status: "completed",
                  note: `Consumed on TopUp day (TopUp bill: ${orderId})`,
                },
                items: consumptionItems,
                ...(dailyHealthRecord && { dailyHealthRecord }),
                summary: {
                  grandTotalAmount: 0,
                  totalOrderVP: 0,
                  shakeBalanceChange: -consumedOnTopUp,
                  remainingShakesAfter: newShakeBalance,
                },
              })
            )
            .catch((e) =>
              console.error("Failed to save TopUp-day consumption record:", e)
            )
        );
      }

      if (isConsumptionTransaction) {
        // Log outward transaction
        bgTasks.push(
          logOutwardTransaction({
            userName: selectedUser.personalInformation?.fullName || selectedUser.personalDetails?.name || "Unknown User",
            userId: selectedUser.id,
            products: selectedProducts.flatMap((p) => {
              if (p.isBundle && p.bundleProducts) {
                return p.bundleProducts.map((bp) => ({
                  productId: bp.productId, productName: bp.productName,
                  quantity: p.quantity, price: bp.price,
                  valuePoints: bp.volPoint, totalAmount: p.quantity * bp.price,
                }));
              }
              return {
                productId: p.productId, productName: p.productName,
                quantity: p.quantity, price: p.productPrice,
                valuePoints: p.volPoint || 0, totalAmount: p.totalPrice,
              };
            }),
            totalAmount: orderTotal.amount,
            totalValuePoints: orderTotal.valuePoints,
          })
            .then(() => loadDashboardStats())
            .catch((e) => console.error("Failed to log outward transaction:", e))
        );

        // Inventory log for each item
        const todayStr = now.toISOString().split("T")[0];
        bgTasks.push(
          Promise.all(
            items.map(async (item) => {
              try {
                const pRef = doc(db, 'products', item.productId);
                const pSnap = await getDoc(pRef);
                const prevStock = pSnap.exists() ? (pSnap.data().stockCount || 0) : 0;
                await addInventoryLog({
                  productId: item.productId, productName: item.productName,
                  date: todayStr, quantityChange: -item.quantity,
                  previousStock: prevStock,
                  newStock: Math.max(0, prevStock - item.quantity),
                  transactionType, referenceId: orderId,
                  customerName: selectedUser.personalInformation?.fullName || selectedUser.personalDetails?.name || "N/A",
                });
              } catch (e) { console.error("Inventory log failed:", e); }
            })
          )
        );
      }

      // Refresh user in background (updates sidebar shake count for next order)
      bgTasks.push(
        getUserById(selectedUser.id)
          .then((u) => {
            if (u) {
              setSelectedUser(u);
              setAllUsers(prev => prev.map(user => user.id === u.id ? u : user));
              setFilteredUsers(prev => prev.map(user => user.id === u.id ? u : user));
            }
          })
          .catch((e) => console.error("Error refreshing user data:", e))
      );

      // Fire-and-forget all background tasks
      Promise.all(bgTasks);

    } catch (error) {
      toast.error("Error placing order");
      console.error(error);
      setIsLoading(false);
    }
  };

  // Reset Form
  const handleReset = () => {
    setSelectedUser(null);
    setSearchTerm("");
    setReferredBySearchTerm("");
    setReferredByUser(null);
    setFilteredUsers(allUsers);
    setUserRecords({ average: 0, records: [] });
    setSelectedCategory(currentDayCategory); // Reset to current day
    setSelectedCategoryType('day');
    setSelectedProducts([]);
    setPaymentMethod("cash");
    setIsSplitPayment(false);
    setSplitAmounts({ cash: "", online: "", gpay: "" });
    setDailyRecord({
      date: new Date().toISOString().split("T")[0],
      weight: "",
      fat: "",
      visceralFat: "",
      muscles: "",
      bmr: "",
      bmi: "",
      bodyAge: "",
      chest: "",
      waistLine: "",
      hip: "",
    });
  };



  return (
    <div className="bg-slate-100 min-h-screen">

      {/* ── POS TOP BAR ── */}
      <div className="bg-slate-900 text-white px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-lg">
        <div>
          <h1 className="text-base font-bold tracking-wide">🧾 POS — Billing Terminal</h1>
          <p className="text-[11px] text-slate-400 mt-0.5">
            {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' })}
          </p>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          {/* Transaction Type */}
          <div className="flex-1 sm:w-52">
            <CustomDropdown
              value={transactionType}
              onChange={setTransactionType}
              options={[
                { value: "TopUp Guest Shake", label: "TopUp Guest Shake" },
                { value: "TopUp Associate Shake", label: "TopUp Associate Shake" },
                { value: "Guest Shake", label: "Guest Shake" },
                { value: "Associate Shake", label: "Associate Shake" },
                { value: "Trial", label: "Trial" },
                { value: "RetailShake", label: "RetailShake" },
                { value: "Donation", label: "Donation" },
                { value: "Sample", label: "Sample" },
              ]}
              placeholder="Transaction type"
            />
          </div>
          <button
            onClick={handleReset}
            className="h-10 px-4 bg-slate-700 hover:bg-slate-600 rounded-xl text-sm font-medium text-white flex items-center gap-2 transition-colors whitespace-nowrap flex-shrink-0"
          >
            <RefreshCw className="w-4 h-4" />
            Reset
          </button>
        </div>
      </div>

      {/* ── POS BODY: Responsive 3-column layout ── */}
      <div className="flex flex-col lg:flex-row h-[calc(100vh-68px)] lg:h-[calc(100vh-68px)] overflow-hidden relative">

        {/* ── COL 1: Category Sidebar (Desktop) / Horizontal Scroll (Mobile) ── */}
        <div className="w-full lg:w-36 xl:w-52 flex-shrink-0 bg-slate-800 lg:overflow-y-auto border-b lg:border-b-0 border-slate-700">
          <div className="px-3 py-1.5 lg:py-2 border-b border-slate-700 hidden lg:block">
            <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Menu</p>
          </div>
          <div className="flex lg:flex-col overflow-x-auto lg:overflow-x-visible p-1.5 space-x-2 lg:space-x-0 lg:space-y-1 no-scrollbar">
            <button
              onClick={() => handleCategorySelect(currentDayCategory, 'day')}
              className={`flex-shrink-0 lg:w-full text-left px-4 lg:px-3 py-2 lg:py-3 rounded-xl lg:rounded-lg transition-all text-xs lg:text-sm ${selectedCategory?.key === currentDayCategory.key && selectedCategoryType === 'day'
                ? 'bg-red-500 text-white font-bold shadow-lg shadow-red-500/30'
                : 'text-slate-300 hover:bg-slate-700 bg-slate-700/50 lg:bg-transparent'
                }`}
            >
              <div className="font-semibold whitespace-nowrap">{currentDayCategory.label}</div>
              <div className="text-[9px] lg:text-[10px] mt-0.5 opacity-60 hidden lg:block">Today ✓</div>
            </button>
            {categories && categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategorySelect(cat, 'product')}
                className={`flex-shrink-0 lg:w-full text-left px-4 lg:px-3 py-2 lg:py-3 rounded-xl lg:rounded-lg transition-all text-xs lg:text-sm ${selectedCategory?.id === cat.id && selectedCategoryType === 'product'
                  ? 'bg-red-500 text-white font-bold shadow-lg shadow-red-500/30'
                  : 'text-slate-300 hover:bg-slate-700 bg-slate-700/50 lg:bg-transparent'
                  }`}
              >
                <div className="font-semibold whitespace-nowrap">{cat.name || cat.categoryName}</div>
              </button>
            ))}
          </div>
        </div>

        {/* ── COL 2: Products + Customer + Health ── */}
        <div className="flex-1 overflow-y-auto bg-slate-100 p-3 space-y-3 min-w-0">

          {/* Product Grid */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between bg-slate-50">
              <h3 className="text-sm font-semibold text-slate-700">
                {selectedCategory
                  ? `${selectedCategory.label || selectedCategory.name || selectedCategory.categoryName}`
                  : 'Select a Category'}
              </h3>
              <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full font-medium">{filteredProducts.length} items</span>
            </div>
            <div className="p-3">
              {filteredProducts.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2">
                  {filteredProducts.map((product) => {
                    const isDayBased = selectedCategoryType === 'day';
                    const displayProduct = isDayBased
                      ? { ...product, name: (product.name || product.productName), productName: (product.name || product.productName) }
                      : product;

                    return (
                      <button
                        key={product.id}
                        onClick={() => handleProductSelect(displayProduct, isDayBased)}
                        className="group relative bg-white border-2 border-slate-100 hover:border-red-400 flex items-center  rounded-xl p-3 text-left transition-all duration-150 hover:shadow-md active:scale-95"
                      >
                        <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center group-hover:bg-red-100 transition-colors">
                          <Package className="w-4 h-4 text-red-500" />
                        </div>
                        <p className="text-[13px] font-semibold text-slate-800 leading-tight pl-2">
                          {product.name || product.productName}
                          {product.isSpecial && (
                            <span className="ml-1 text-amber-500" title="Special Product">★</span>
                          )}
                        </p>
                        {/* {product.mrp && (
                          <p className="text-[11px] text-slate-400 mt-1 font-medium">₹{product.mrp}</p>
                        )} */}
                        <div className="absolute top-2 right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <Plus className="w-3 h-3 text-white" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Package className="w-8 h-8 mx-auto mb-2 text-slate-200" />
                  <p className="text-sm text-slate-400">
                    {selectedCategory ? 'No products available' : 'Select a category'}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Special Products Section — always shown */}
          {specialProducts.length > 0 && (
            <div className="bg-white rounded-xl border border-amber-200 overflow-hidden shadow-sm">
              <div className="px-4 py-2.5 border-b border-amber-100 flex items-center justify-between bg-amber-50">
                <h3 className="text-sm font-semibold text-amber-700 flex items-center gap-1.5">
                  <span>★</span> Special Products
                </h3>
                <span className="text-xs bg-amber-100 text-amber-600 px-2 py-0.5 rounded-full font-medium">
                  {specialProducts.length} items
                </span>
              </div>
              <div className="p-3">
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2">
                  {specialProducts.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => handleProductSelect(product, false)}
                      className="group relative bg-white border-2 border-amber-100 flex items-center hover:border-amber-400 rounded-xl p-3 text-left transition-all duration-150 hover:shadow-md active:scale-95"
                    >
                      <div className="w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center group-hover:bg-amber-100 transition-colors">
                        <Package className="w-4 h-4 text-amber-500" />
                      </div>
                      <p className="text-[13px] font-semibold text-slate-800 leading-tight pl-2">
                        {product.name || product.productName}
                        <span className="ml-1 text-amber-500" title="Special Product">★</span>
                      </p>
                      {/* {product.mrp && (
                        <p className="text-[11px] text-slate-400 mt-1 font-medium">₹{product.mrp}</p>
                      )} */}
                      <div className="absolute top-2 right-2 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <Plus className="w-3 h-3 text-white" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Customer Selection */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50">
              <h3 className="text-sm font-semibold text-slate-700">👤 Customer</h3>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5">Referred By</label>
                  <div className="flex gap-2">
                    <div className="flex-1 relative" onBlur={(e) => {
                      if (!e.currentTarget.contains(e.relatedTarget)) {
                        setShowReferredByDropdown(false);
                      }
                    }}>
                      <div onFocus={() => setShowReferredByDropdown(true)}>
                        <input
                          type="text"
                          value={referredBySearchTerm}
                          onChange={(e) => {
                            const val = e.target.value;
                            setReferredBySearchTerm(val);
                            setShowReferredByDropdown(true);
                            if (!val) {
                              setReferredByUser(null);
                              setFilteredUsers(allUsers);
                            } else {
                              setReferredByUser({ name: val, id: null });
                              setFilteredUsers(allUsers.filter(user =>
                                user.accountSecurity?.referredBy?.toLowerCase().includes(val.toLowerCase())
                              ));
                            }
                            setSelectedUser(null);
                          }}
                          placeholder="Search or enter referrer name"
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400 outline-none"
                        />
                      </div>
                      {showReferredByDropdown && getFilteredReferrers(referredBySearchTerm).length > 0 && (
                        <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-xl shadow-lg mt-1 max-h-48 overflow-y-auto">
                          {getFilteredReferrers(referredBySearchTerm).map(u => {
                            const name = u.personalInformation?.fullName || u.personalDetails?.name || u.name || "Unknown";
                            const mobile = u.personalInformation?.mobileNumber || u.personalDetails?.mobile || u.mobile || "No Mobile";
                            const extra = u.customerNumber || u.accountSecurity?.role || u.role || 'User';
                            return (
                              <button
                                key={u.id}
                                type="button"
                                className="w-full text-left px-4 py-2 hover:bg-gray-50 focus:bg-gray-50 transition-colors border-b border-gray-50 last:border-0"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  setReferredBySearchTerm(name);
                                  setReferredByUser({ name, id: u.id });
                                  setFilteredUsers(allUsers.filter(user =>
                                    user.accountSecurity?.referredBy?.toLowerCase() === name.toLowerCase()
                                  ));
                                  setSelectedUser(null);
                                  setShowReferredByDropdown(false);
                                }}
                              >
                                <div className="font-medium text-sm text-gray-900">{name}</div>
                                <div className="text-xs text-gray-500">{mobile} • {extra}</div>
                              </button>
                            )
                          })}
                        </div>
                      )}
                    </div>
                    {(referredByUser || referredBySearchTerm) && (
                      <button
                        onClick={() => clearReferredByFilter()}
                        className="px-3 bg-red-50 text-red-500 rounded-lg border border-red-100 hover:bg-red-100 text-xs font-semibold shrink-0"
                      >Clear</button>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5">
                    Select User {referredByUser && <span className="text-slate-400">({filteredUsers.length})</span>}
                  </label>
                  <div className="relative dropdown-container">
                    <input
                      type="text"
                      value={searchTerm}
                      onClick={() => setShowDropdown(true)}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setShowDropdown(true);
                        setSelectedUser(null);
                      }}
                      placeholder="Type name or mobile..."
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400 outline-none"
                    />
                    {showDropdown && (
                      <div className="absolute z-10 w-full mt-1 max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg">
                        {filteredUsers.length > 0 ? (
                          filteredUsers.map((u) => (
                            <button
                              key={u.id}
                              onClick={() => handleSelectUser(u)}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-red-50 focus:bg-red-50 focus:outline-none"
                            >
                              <div className="font-semibold text-slate-800">
                                {u.personalInformation?.fullName || u.personalDetails?.name || 'No Name'}
                              </div>
                              <div className="text-[10px] text-slate-500">
                                {u.personalInformation?.mobileNumber || u.personalDetails?.mobile || 'No Mobile'}
                              </div>
                            </button>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-sm text-slate-500 text-center">No users found</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {selectedUser ? (
                <div className="mt-3">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[
                      { label: 'Name', value: selectedUser.personalInformation?.fullName || selectedUser.personalDetails?.name || 'N/A', color: 'text-slate-900' },
                      { label: 'Mobile', value: selectedUser.personalInformation?.mobileNumber || selectedUser.personalDetails?.mobile || 'N/A', color: 'text-slate-700' },
                      { label: 'Shakes Left', value: `${selectedUser.inventory?.remainingShakes || 0}`, color: 'text-green-600' },
                      { label: 'Last Weight', value: `${lastOrderWeight || selectedUser.currentHealthMetrics?.lastWeight || userRecords.average || 'N/A'} kg`, color: 'text-blue-600' },
                    ].map(({ label, value, color }) => (
                      <div key={label} className="bg-slate-50 rounded-lg px-3 py-2 border border-slate-100">
                        <p className="text-[10px] text-slate-400 font-medium uppercase">{label}</p>
                        <p className={`text-sm font-bold mt-0.5 truncate ${color}`}>{value}</p>
                      </div>
                    ))}
                  </div>

                  {/* Complementary shake counter badge */}
                  {transactionType === "Trial" && (
                    <div className={`mt-2 flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-semibold ${complementaryShakesUsed >= 3
                      ? 'bg-red-50 border-red-200 text-red-700'
                      : complementaryShakesUsed >= 2
                        ? 'bg-orange-50 border-orange-200 text-orange-700'
                        : 'bg-green-50 border-green-200 text-green-700'
                      }`}>
                      <span>🍵 TRIAL Shakes Used:</span>
                      <span className="text-base font-extrabold">{complementaryShakesUsed}</span>
                      <span>/ 3</span>
                      {complementaryShakesUsed >= 3 && (
                        <span className="ml-auto px-2 py-0.5 bg-red-100 text-red-600 rounded-full text-[10px] font-bold uppercase tracking-wide">Limit Reached</span>
                      )}
                      {complementaryShakesUsed < 3 && (
                        <span className="ml-auto px-2 py-0.5 bg-current/10 rounded-full text-[10px] font-bold">{3 - complementaryShakesUsed} remaining</span>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-100 text-center">
                  <p className="text-xs text-slate-400">🔍 Search and select a customer</p>
                </div>
              )}
            </div>
          </div>

          {/* Daily Health Record */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-700">📊 Health Record</h3>
              <input
                type="date"
                value={dailyRecord.date}
                onChange={(e) => handleDailyRecordChange("date", e.target.value)}
                className="text-xs border border-slate-200 rounded-lg px-2 py-1 outline-none focus:ring-2 focus:ring-red-400"
              />
            </div>
            <div className="p-3">
              <div className="grid grid-cols-3 md:grid-cols-4 sm:grid-cols-5 xl:grid-cols-10 gap-2">
                {[
                  { field: 'weight', label: 'Weight', unit: 'kg' },
                  { field: 'height', label: 'Height', unit: 'cm' },
                  { field: 'fat', label: 'Fat', unit: '%' },
                  { field: 'visceralFat', label: 'Visceral', unit: '' },
                  { field: 'muscles', label: 'Muscle', unit: '%' },
                  { field: 'bmr', label: 'BMR', unit: '' },
                  { field: 'bodyAge', label: 'Age', unit: '' },
                  { field: 'chest', label: 'Chest', unit: 'in' },
                  { field: 'waistLine', label: 'Waist', unit: 'in' },
                  { field: 'hip', label: 'Hip', unit: 'in' },
                  { field: 'bmi', label: 'BMI', unit: '' },
                ].map(({ field, label, unit }) => (
                  <div key={field}>
                    <label className="block text-[10px] font-semibold text-slate-500 mb-1">{label}{unit ? ` (${unit})` : ''}</label>
                    <input
                      type="number"
                      step="1"
                      value={dailyRecord[field]}
                      onChange={(e) => handleDailyRecordChange(field, e.target.value)}
                      placeholder="0"
                      className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-red-400 focus:border-red-400"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>{/* end center */}

        {/* ── COL 3: Cart / Checkout Panel (Desktop Drawer / Mobile Full-screen) ── */}
        <div className={`
          fixed lg:static inset-0 z-40 lg:z-auto
          w-full lg:w-80 xl:w-96 flex-shrink-0 
          bg-white flex flex-col border-l border-slate-200 shadow-2xl lg:shadow-xl
          transition-transform duration-300 ease-in-out
          ${showMobileCart ? 'translate-y-0' : 'translate-y-full lg:translate-y-0'}
        `}>

          {/* Cart Header */}
          <div className="flex items-center justify-between px-4 py-4 lg:py-3 bg-slate-900 border-b border-slate-700">
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 lg:w-4 lg:h-4 text-white" />
              <h3 className="text-sm lg:text-sm font-semibold text-white">
                Order ({selectedProducts.length} items)
              </h3>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSelectedProducts([])}
                className="text-xs text-red-300 hover:text-white hover:bg-red-600 px-2.5 py-1.5 rounded-lg transition-colors"
              >
                Clear All
              </button>
              <button
                onClick={() => setShowMobileCart(false)}
                className="lg:hidden text-slate-400 hover:text-white p-1.5"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {selectedProducts.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full py-16 text-center">
                <ShoppingCart className="w-12 h-12 text-slate-200 mb-3" />
                <p className="text-sm text-slate-400 font-medium">Cart is empty</p>
                <p className="text-xs text-slate-300 mt-1">Select products to add</p>
              </div>
            ) : (
              selectedProducts.map((product) => (
                <div
                  key={product.id}
                  className="flex items-center gap-2 bg-slate-50 p-2.5 rounded-xl border border-slate-100"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-slate-800 truncate">{product.productName}</div>
                    <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                      {product.discount === 100 && (
                        <div className="flex items-center gap-1">
                          <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-[9px] font-bold rounded-full">
                            {product.specialType ? product.specialType.toUpperCase() : 'TRIAL'}
                          </span>
                          <button
                            onClick={() => setEditingSpecialTypeId(product.id)}
                            className="text-[9px] text-blue-500 hover:text-blue-700 font-bold cursor-pointer underline"
                          >
                            Edit
                          </button>
                        </div>
                      )}
                      {product.discount > 0 && product.discount < 100 && (
                        <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-[9px] font-bold rounded-full">{product.discount}% OFF</span>
                      )}
                      {product.days && (
                        <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[9px] font-bold rounded-full">{product.days}d</span>
                      )}
                      <span className="text-[10px] text-slate-400">₹{product.productPrice.toFixed(0)} × {product.quantity}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 bg-white rounded-lg border border-slate-200 flex-shrink-0">
                    <button onClick={() => handleUpdateQuantity(product.id, -1)} className="p-2.5 lg:p-1.5 hover:bg-slate-100 rounded-l-lg transition-all active:bg-slate-200">
                      <Minus className="w-4 h-4 lg:w-3 h-3 text-slate-600" />
                    </button>
                    <span className="px-2 text-sm lg:text-xs font-bold text-slate-900 min-w-[1.5rem] lg:min-w-[1.2rem] text-center">{product.quantity}</span>
                    <button onClick={() => handleUpdateQuantity(product.id, 1)} className="p-2.5 lg:p-1.5 hover:bg-slate-100 rounded-r-lg transition-all active:bg-slate-200">
                      <Plus className="w-4 h-4 lg:w-3 h-3 text-slate-600" />
                    </button>
                  </div>
                  <div className="text-right flex-shrink-0 min-w-[58px]">
                    <div className="text-xs lg:text-[11px] font-bold text-slate-900">₹{product.totalPrice.toFixed(0)}</div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!product.isBundle && products.find(p => p.id === product.productId)?.isSpecial && (
                      <button onClick={() => handleEditProduct(product)} className="px-2 py-1 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded text-[9px] font-bold uppercase tracking-wider transition-colors border border-blue-100">
                        Edit
                      </button>
                    )}
                    <button onClick={() => handleRemoveProduct(product.id)} className="p-2 lg:p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                      <Trash2 className="w-4 h-4 lg:w-3.5 lg:h-3.5" />
                    </button>
                  </div>
                </div>
              )))}
          </div>

          {/* Payment + Totals + Checkout */}
          <div className="border-t border-slate-100 bg-slate-50 p-4 space-y-3">
            {/* Payment Method */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Payment Method</p>
                {transactionType !== "Trial" && transactionType !== "Donation" && (
                  <button
                    onClick={() => {
                      setIsSplitPayment(!isSplitPayment);
                      if (!isSplitPayment) {
                        setSplitAmounts({ cash: orderTotal.amount, online: "", gpay: "" });
                      }
                    }}
                    className="text-[10px] font-bold text-blue-500 hover:text-blue-600 underline cursor-pointer"
                  >
                    {isSplitPayment ? "Single Method" : "Split Payment"}
                  </button>
                )}
              </div>

              {transactionType === "Trial" || transactionType === "Donation" ? (
                <div className="w-full py-2 bg-slate-100 border-2 border-slate-200 rounded-lg text-center cursor-not-allowed">
                  <span className="text-xs font-bold text-slate-500 uppercase">{transactionType}</span>
                </div>
              ) : isSplitPayment ? (
                <div className="space-y-2">
                  {['cash', 'online', 'gpay'].map((method) => (
                    <div key={method} className="flex items-center gap-2">
                      <span className="w-16 text-xs font-bold text-slate-700 capitalize">{method === 'gpay' ? 'GPay' : method}</span>
                      <div className="relative flex-1 bg-white border-2 border-slate-200 rounded-lg overflow-hidden focus-within:border-blue-500 transition-all">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-slate-400 font-semibold text-sm">₹</span>
                        </div>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          placeholder="0"
                          value={splitAmounts[method]}
                          onChange={(e) => setSplitAmounts({ ...splitAmounts, [method]: e.target.value })}
                          className="w-full pl-7 pr-3 py-1.5 text-sm font-semibold text-slate-800 focus:outline-none"
                        />
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-between items-center text-xs font-bold pt-1">
                    <span className="text-slate-500 uppercase">Split Total</span>
                    <span className={`${Math.abs(Object.values(splitAmounts).reduce((sum, val) => sum + (parseFloat(val) || 0), 0) - orderTotal.amount) > 0.1
                      ? 'text-red-500'
                      : 'text-green-500'
                      }`}>
                      ₹{Object.values(splitAmounts).reduce((sum, val) => sum + (parseFloat(val) || 0), 0).toFixed(0)} / ₹{orderTotal.amount.toFixed(0)}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-1.5">
                  {['cash', 'online', 'gpay'].map((method) => (
                    <button
                      key={method}
                      onClick={() => setPaymentMethod(method)}
                      className={`py-2 text-xs font-semibold rounded-lg border-2 transition-all capitalize ${paymentMethod === method
                        ? 'bg-slate-900 text-white border-slate-900 shadow-md'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                        }`}
                    >
                      {method === 'gpay' ? 'GPay' : method.charAt(0).toUpperCase() + method.slice(1)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Low Quantity Alert */}
            {lowQuantityWarning && (
              <div className="flex items-start gap-2 px-3 py-2.5 bg-orange-50 border border-orange-300 rounded-xl text-xs text-orange-800 font-medium animate-pulse">
                <span className="text-base leading-none mt-0.5">⚠️</span>
                <div>
                  <div className="font-bold text-orange-700">Low Balance Warning</div>
                  <div className="text-orange-600 mt-0.5">
                    You selected <strong>{orderedQuantity}</strong> shake{orderedQuantity !== 1 ? 's' : ''} but the user only has <strong>{availableShakes}</strong> remaining.
                  </div>
                </div>
              </div>
            )}

            {/* Order Totals */}
            <div className="bg-white rounded-xl border border-slate-100 p-3 space-y-2">
              <div className="flex justify-between text-xs text-slate-500">
                <span>Total Items</span>
                <span className="font-semibold text-slate-800">{orderTotal.quantity}</span>
              </div>
              {selectedUser && isConsumptionType && (
                <div className="flex justify-between text-xs text-slate-400">
                  <span>Shake Balance</span>
                  <span className={`font-semibold ${availableShakes === 0 ? 'text-red-500' :
                    orderedQuantity > availableShakes ? 'text-orange-500' : 'text-green-600'
                    }`}>{availableShakes} available</span>
                </div>
              )}
              {orderTotal.freeShakesUsed > 0 && (
                <div className="flex justify-between text-xs text-green-600">
                  <span>Shakes Used (Product Given)</span>
                  <span className="font-semibold">{orderTotal.freeShakesUsed}</span>
                </div>
              )}
              {orderTotal.chargedQuantity > 0 && orderTotal.freeShakesUsed > 0 && (
                <div className="flex justify-between text-xs text-orange-600">
                  <span>Charged Qty</span>
                  <span className="font-semibold">{orderTotal.chargedQuantity}</span>
                </div>
              )}
              <div className="border-t border-slate-100 pt-2 flex justify-between items-end">
                <div>
                  <span className="text-sm font-bold text-slate-700">Total Amount</span>
                  {orderTotal.freeShakesUsed > 0 && orderTotal.amount === 0 && (
                    <div className="text-[10px] text-green-600 font-bold tracking-wide mt-0.5">COVERED BY BALANCE</div>
                  )}
                </div>
                <div className="text-right">
                  {orderTotal.freeShakesUsed > 0 && orderTotal.amount === 0 ? (
                    <span className="text-lg font-extrabold text-blue-600">₹{orderTotal.originalAmount.toFixed(2)}</span>
                  ) : orderTotal.amount < orderTotal.originalAmount ? (
                    <div className="flex flex-col items-end">
                      <span className="text-xs text-slate-400 line-through">₹{orderTotal.originalAmount.toFixed(2)}</span>
                      <span className="text-lg font-extrabold text-blue-600">₹{orderTotal.amount.toFixed(2)}</span>
                    </div>
                  ) : (
                    <span className="text-lg font-extrabold text-blue-600">₹{orderTotal.amount.toFixed(2)}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Guest Shake — no shakes left warning */}
            {transactionType === "Guest Shake" && selectedUser && (selectedUser.inventory?.remainingShakes || 0) === 0 && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 text-xs font-semibold text-red-700">
                <span>🚫</span>
                <span>No shakes left — recharge this account before placing a Guest Shake order.</span>
              </div>
            )}

            {/* Place Order */}
            <button
              onClick={handlePlaceOrder}
              disabled={
                isLoading ||
                !selectedUser ||
                selectedProducts.length === 0 ||
                (transactionType === "Guest Shake" && (selectedUser?.inventory?.remainingShakes || 0) === 0)
              }
              className="w-full h-12 bg-red-500 hover:bg-red-600 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg shadow-red-500/20 text-sm"
            >
              <ShoppingCart className="w-4 h-4" />
              {isLoading ? 'Placing Order...' : 'Place Order'}
            </button>
          </div>

        </div>{/* end cart panel */}
      </div>{/* end 3-col flex */}

      {/* ── MOBILE STICKY FOOTER ── */}
      {selectedProducts.length > 0 && !showMobileCart && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 shadow-[0_-4px_10px_rgba(0,0,0,0.05)] z-30 flex items-center justify-between animate-in slide-in-from-bottom duration-300">
          <div className="flex flex-col">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Cart Total</span>
            <span className="text-lg font-extrabold text-blue-600">₹{orderTotal.amount.toFixed(0)}</span>
          </div>
          <button
            onClick={() => setShowMobileCart(true)}
            className="bg-slate-900 text-white px-6 py-2.5 rounded-xl font-bold text-sm shadow-lg flex items-center gap-2 active:scale-95 transition-all"
          >
            <ShoppingCart className="w-4 h-4" />
            View Order ({selectedProducts.length})
          </button>
        </div>
      )}

      {/* Duration / Discount Modal */}
      {showDurationModal && selectedProductForDuration && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="bg-slate-900 px-5 py-4 rounded-t-2xl flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-white">
                  {modalStep === 'discount' ? 'Select Discount' : 'Select Duration'}
                </h2>
                <p className="text-xs text-slate-400 mt-0.5">
                  {isBundleSelection && Array.isArray(selectedProductForDuration)
                    ? selectedProductForDuration.map(p => p.name || p.productName).join(' + ')
                    : (selectedProductForDuration.name || selectedProductForDuration.productName)}
                  {modalStep === 'duration' && selectedDiscount > 0 && (
                    <span className="ml-2 text-green-400">• {selectedDiscount === 100 ? 'TRIAL' : `${selectedDiscount}% OFF`}</span>
                  )}
                </p>
              </div>
              <button
                onClick={() => { setShowDurationModal(false); setSelectedProductForDuration(null); setSelectedDiscount(0); setModalStep('discount'); }}
                className="text-slate-400 hover:text-white hover:bg-white/10 rounded-xl p-2 transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5">
              {modalStep === 'discount' ? (
                <div className="space-y-3">
                  <p className="text-sm text-slate-600 font-medium mb-4">Select discount percentage</p>
                  <button
                    onClick={() => handleDiscountSelect(0)}
                    className="group w-full bg-slate-50 hover:bg-slate-100 border-2 border-slate-100 rounded-2xl p-4 text-left transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center"><span className="text-lg">💰</span></div>
                        <div>
                          <div className="text-sm font-bold text-slate-900">No Discount</div>
                          <div className="text-[11px] text-slate-500">Full price</div>
                        </div>
                      </div>
                      <div className="text-lg font-bold text-slate-900">
                        ₹{parseFloat(selectedProductForDuration.mrp || selectedProductForDuration.price || 0).toFixed(2)}
                      </div>
                    </div>
                  </button>
                  <div className="relative"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-100"></div></div>
                    <div className="relative flex justify-center"><span className="bg-white px-2 text-xs font-bold text-slate-400 uppercase">Or Apply Discount</span></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {[15, 25, 35, 42, 50, 100].map((discount) => {
                      const orig = parseFloat(selectedProductForDuration.mrp || selectedProductForDuration.price || 0);
                      const disc = orig * (1 - discount / 100);
                      return (
                        <button key={discount} onClick={() => handleDiscountSelect(discount)}
                          className="bg-white hover:bg-green-50 border-2 border-slate-100 hover:border-green-400 rounded-2xl p-3 text-left transition-all hover:shadow-md">
                          <div className="flex justify-between items-start mb-2">
                            <span className="px-2 py-1 rounded-lg bg-green-100 text-green-700 text-xs font-bold">
                              {discount === 100 ? 'TRIAL' : `${discount}% OFF`}
                            </span>
                            <span className="text-[10px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Save ₹{(orig - disc).toFixed(0)}</span>
                          </div>
                          <div className="text-[10px] text-slate-400 line-through">₹{orig.toFixed(2)}</div>
                          <div className="text-base font-bold text-slate-900">₹{disc.toFixed(0)}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm text-slate-600 font-medium">How many days?</p>
                    {!isBundleSelection && (
                      <button onClick={() => setModalStep('discount')} className="text-xs text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1">
                        <ChevronLeft className="w-3 h-3" /> Change Discount
                      </button>
                    )}
                  </div>
                  {/* Balance hint */}
                  {selectedUser && (isConsumptionType || transactionType === "Guest Shake" || transactionType === "Associate Shake") && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-100 rounded-xl text-xs text-blue-700 font-medium mb-1">
                      <span>🧃</span>
                      <span>Available balance: <strong>{availableShakes}</strong> shake{availableShakes !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                  {/* Plan info banner — shows stored package or inferred plan */}
                  {(() => {
                    const effDays = isBundleSelection && isConsumptionType ? getEffectivePlan(selectedUser) : null;
                    const pkg = effDays ? SHAKE_PACKAGES[effDays] : null;
                    const storedName = selectedUser?.inventory?.shakePackage;
                    const displayName = storedName || (pkg?.name ?? null);
                    const isInferred = !selectedUser?.inventory?.shakePackage && !selectedUser?.inventory?.topUpPlan;
                    return pkg ? (
                      <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-xl text-xs text-green-800 font-medium mb-1">
                        <span>🎫</span>
                        <span>
                          <strong>{displayName} Plan</strong>
                          {' · '}{pkg.days} days @ <strong>₹{pkg.pricePerDay}/day</strong>
                          {isInferred && <span className="ml-1 opacity-60">(inferred)</span>}
                        </span>
                      </div>
                    ) : null;
                  })()}
                  {/* Bundle pricing notice for TopUp transactions */}
                  {isBundleSelection && !isConsumptionType && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50 border border-indigo-100 rounded-xl text-xs text-indigo-700 font-medium mb-1">
                      <span>💡</span>
                      <span>Bundle pricing — select the plan to top up</span>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    {[1, 5, 15, 25].map((days) => {
                      let totalPrice, pricePerDay;
                      if (isBundleSelection) {
                        // Consumption: use effective plan rate (stored topUpPlan OR inferred from shakes)
                        const effPlan = isConsumptionType ? getEffectivePlan(selectedUser) : null;
                        if (effPlan && BUNDLE_PRICING[effPlan]) {
                          pricePerDay = BUNDLE_PRICING[effPlan] / effPlan;
                          totalPrice = Math.round(pricePerDay * days);
                        } else {
                          // TopUp or no plan: standard BUNDLE_PRICING
                          totalPrice = BUNDLE_PRICING[days] || 0;
                          pricePerDay = totalPrice / days;
                        }
                      } else {
                        const orig = parseFloat(selectedProductForDuration.mrp || selectedProductForDuration.price || 0);
                        pricePerDay = orig * (1 - selectedDiscount / 100);
                        totalPrice = pricePerDay * days;
                      }
                      const exceedsBalance = selectedUser && isConsumptionType && days > availableShakes;
                      const pkg = SHAKE_PACKAGES[days];
                      return (
                        <button key={days} onClick={() => handleDurationSelect(days)}
                          className={`relative border-2 rounded-2xl p-4 text-left transition-all hover:shadow-md ${exceedsBalance
                            ? 'bg-orange-50 border-orange-300 hover:border-orange-400'
                            : 'bg-white hover:bg-blue-50 border-slate-100 hover:border-blue-400'
                            }`}>
                          {exceedsBalance && (
                            <div className="absolute top-2 right-2 bg-orange-400 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                              TopUp
                            </div>
                          )}
                          {/* Package name badge */}
                          {isBundleSelection && pkg && (
                            <div className={`text-[10px] font-bold uppercase tracking-wide mb-1.5 ${exceedsBalance ? 'text-orange-500' : 'text-indigo-500'
                              }`}>{pkg.name}</div>
                          )}
                          <div className="flex items-center justify-between mb-2">
                            <span className={`px-3 py-1.5 rounded-lg text-sm font-bold ${exceedsBalance ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                              }`}>{days} Day{days > 1 ? 's' : ''}</span>
                            <span className={`text-xs font-bold px-2 py-1 rounded-full ${exceedsBalance ? 'bg-orange-50 text-orange-600' : 'bg-blue-50 text-blue-600'
                              }`}>Qty: {days}</span>
                          </div>
                          <div className="text-xs text-slate-500 mb-1">Total Amount</div>
                          <div className="text-lg font-bold text-slate-900">₹{totalPrice.toLocaleString()}</div>
                          <div className="text-[10px] text-slate-400">₹{pricePerDay.toFixed(2)} × {days}
                            {!isBundleSelection && selectedDiscount > 0 && <span className="ml-1 text-green-600">({selectedDiscount === 100 ? 'TRIAL' : `${selectedDiscount}% off`})</span>}
                            {isBundleSelection && <span className="ml-1 text-blue-600">(Bundle)</span>}
                          </div>
                          {exceedsBalance && (
                            <div className="mt-1.5 text-[9px] text-orange-600 font-semibold">⚠️ Exceeds balance · Will switch to TopUp</div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Invoice Receipt Modal */}
      {showSuccessModal && orderSummary && (
        <InvoiceReceipt
          transaction={{
            billNo: orderSummary.billNo,
            id: orderSummary.id,
            createdAt: new Date().toISOString(),
            userName: orderSummary.user.name,
            userMobile: orderSummary.user.mobile,
            transactionType: orderSummary.transactionType,
            products: orderSummary.products.map((p) => ({
              productName: p.productName,
              quantity: p.quantity,
              price: p.pricePerUnit,
              originalPrice: p.originalPrice,
              mrp: p.pricePerUnit,
              discount: p.discount,
              specialType: p.specialType,
            })),
            totalShakeQuantity: orderSummary.total.quantity,
            totalAmount: orderSummary.total.amount,
            paymentMethod: orderSummary.paymentMethod,
            status: "active",
            previousShakeQuantity: orderSummary.previousShakeQuantity,
            newShakeQuantity: orderSummary.newShakeQuantity,
            dailyRecord: orderSummary.dailyRecord,
            dailySerial: orderSummary.dailySerial,
          }}
          autoPrint={true}
          onClose={() => {
            setShowSuccessModal(false);
            handleReset();
            // Stay on POS — no navigation
          }}
        />
      )}
      {/* Special Type Edit Modal */}
      {editingSpecialTypeId && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Select Special Type</h3>
            <div className="space-y-2">
              {['Trial', 'Sample', 'Product Given'].map(type => (
                <button
                  key={type}
                  onClick={() => {
                    setSelectedProducts(prev => prev.map(p =>
                      p.id === editingSpecialTypeId ? { ...p, specialType: type } : p
                    ));
                    setEditingSpecialTypeId(null);
                  }}
                  className="w-full py-3 px-4 text-left font-semibold text-slate-700 bg-slate-50 hover:bg-blue-50 hover:text-blue-600 rounded-xl transition-colors border border-slate-100"
                >
                  {type}
                </button>
              ))}
            </div>
            <button
              onClick={() => setEditingSpecialTypeId(null)}
              className="mt-4 w-full py-2 text-center text-sm font-semibold text-slate-500 hover:text-slate-700"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRecharge;
